import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TenantService } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { isArray } from '../utils/utils';
import { IPrintJob } from './models/print-job.model';
import * as _ from 'lodash';
import { Observable, Subscriber, timer } from 'rxjs';
import { delay, finalize, retry, retryWhen, take, tap } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { SanitizationService } from '../sanitization/sanitization.service';

export const PRINT_JOB_STATUS = [
{
  status: "RELEASING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "RELEASE_DELIVERY_FAILED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "RELEASE_FAILED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "RELEASE_TIMED_OUT",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "RELEASED",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "AWAIT_PRINT",
  showStatusInfo: false,
  showLoader: false,
  isSuccess: false,
  isError: false
},
{
  status: "PRINTING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "PRINTING_FAILING",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "PRINTED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: true,
  isError: false
},
{
  status: "DELETING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "DELETE_FAILED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "DELETE_TIMED_OUT",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "DELETED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: true,
  isError: false
},
{
  status: "QUEUEING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "TRANSFERING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "UPLOADING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "UPLOAD_FAILED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "UPLOAD_TIMED_OUT",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "UPLOADED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: true,
  isError: false
},
{
  status: "CONVERTING",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "CONVERTED",
  showStatusInfo: false,
  showLoader: false,
  isSuccess: false,
  isError: false
},
{
  status: "PRINT_OK",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: true,
  isError: false
},
{
  status: "PRINT_CANCELLED",
  showStatusInfo: false,
  showLoader: false,
  isSuccess: false,
  isError: false
},
{
  status: "PRINT_FAILED",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "PRINT_OK_SENT_TO_QUEUE",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: false
},
{
  status: "PRINT_OK_SENT_TO_SERVER",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
},
{
  status: "PRINT_FAILED_SENT_TO_QUEUE",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "PRINT_FAILED_SENT_TO_SERVER",
  showStatusInfo: true,
  showLoader: false,
  isSuccess: false,
  isError: true
},
{
  status: "IN_CLOUD_STORAGE",
  showStatusInfo: false,
  showLoader: false,
  isSuccess: false,
  isError: false
},
{
  // In-progress state: print job accepted and being sent onward (still running).
  status: "IN_PROGRESS_SENT",
  showStatusInfo: true,
  showLoader: true,
  isSuccess: false,
  isError: false
}
];

@Injectable({
  providedIn: 'root'
})

export class PrintJobService {

  public currentNetwork: Array<string> = [];
  public PRINT_JOB_APP_STATES: Array<string> = [];
  private httpErrorResponse: HttpErrorResponse;
  public tenant = this.tenantService.tenantSignal;
  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private tenantService: TenantService,
    private loggingService: LoggingService,
    private userService: UserService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = this.loggingService.getLogger("[PrintJobService]");
    const methodName = "ctor";
    this.logger.entry(methodName);

    this.PRINT_JOB_APP_STATES = [
      "PRINT_OK",
      "PRINT_FAILED",
      "CONVERTED",
      "IN_PROGRESS_SENT"
    ];
  }

  private deserializeLinkObjects(printJob: IPrintJob, links: any): void {
    if (!links) {
      return;
    }

    const linkMappings: Array<{ key: string; target: (url: string | null) => void }> = [
      { key: 'self', target: (url) => printJob.links.self = url },
      { key: 'px:printer', target: (url) => printJob.links.printer = url },
      { key: 'px:workstation', target: (url) => printJob.links.workstation = url },
      { key: 'px:queue', target: (url) => printJob.links.queue = url },
      { key: 'px:finishedRelease', target: (url) => printJob.links.finishedRelease = url }
    ];

    linkMappings.forEach(({ key, target }) => {
      const href = links[key]?.href;
      if (href) {
        target(this.sanitizationService.validateApiUrl(href));
      }
    });

    // Handle ongoingRelease array separately
    if (links['px:ongoingRelease']) {
      if (links['px:ongoingRelease'].href) {
        const validUrl = this.sanitizationService.validateApiUrl(links['px:ongoingRelease'].href);
        if (validUrl) {
          printJob.links.ongoingRelease.push({ href: validUrl });
        }
      } else if (isArray(links['px:ongoingRelease'])) {
        printJob.links.ongoingRelease = links['px:ongoingRelease']
          .map(release => {
            const validUrl = this.sanitizationService.validateApiUrl(release?.href);
            return validUrl ? { href: validUrl } : null;
          })
          .filter(url => url !== null);
      }
    }
  }

  /**
   * Safely sanitizes non-display text fields from API responses to prevent XSS.
   * Strips HTML tags and dangerous characters. Use for fields NOT shown in templates.
   */
  private sanitizeTextField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeTextInput(value);
    return sanitized || null;
  }

  /**
   * Sanitizes user-provided text fields that will be displayed in templates.
   * Preserves all characters so they render as visible plain text.
   */
  private sanitizeDisplayField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeDisplayText(value);
    return sanitized || null;
  }

  private deserializeEmbeddedObjects(printJob: IPrintJob, embedded: any): void {
    if (embedded) {
    }
  }

  private setJobState = function (printJob: IPrintJob) {
    const matchedStatus = _.find(PRINT_JOB_STATUS, (statusObj) => {
      return statusObj.status === printJob.jobState;
    });
    // Guard against unrecognized/null job states (e.g. a newly introduced server
    // state not yet listed in PRINT_JOB_STATUS): keep the safe default created in
    // deserializePrintJob() instead of assigning undefined, which would crash the
    // template when it reads printJobStatus.showStatusInfo.
    if (matchedStatus) {
      printJob.options.printJobStatus = matchedStatus;
    }
  };

  public deserializePrintJob(input: any): IPrintJob {
    const printJob: IPrintJob = {
      links: {
        self: null,
        printer: null,
        workstation: null,
        queue: null,
        ongoingRelease: [],
        finishedRelease: null
      },
      color: input.color ? input.color : false,
      cloudStoreReferences: input.cloudStoreReferences ? input.cloudStoreReferences : [],
      deletedTime: input.deletedTime ? input.deletedTime : null,
      duplex: input.duplex ? input.duplex : false,
      jobName: this.sanitizeDisplayField(input.jobName),
      jobState: input.jobState ? input.jobState : null,
      jobNetwork: this.currentNetwork ? this.currentNetwork : [],
      pageCount: input.pageCount ? input.pageCount : 0,
      paperSize: input.paperSize ? input.paperSize : null,
      pdl: input.pdl ? input.pdl : null,
      pinned: input.pinned ? input.pinned : false,
      restoreTime: input.restoreTime ? input.restoreTime : null,
      submitTime: input.submitTime ? input.submitTime : null,
      options: {
        isSelectedForRestore: false,
        isSelected: false,
        resourceErrors: [],
        printJobStatus: {
          status: null,
          showLoader: false,
          isError: false,
          isSuccess: false,
          showStatusInfo: false
        }
      }
    };
    if (input._links) {this.deserializeLinkObjects(printJob, input._links);}
    if (input._embedded) {this.deserializeEmbeddedObjects(printJob, input._embedded);}
    this.setJobState(printJob);
    return printJob;
  }

  private deserializePrintJobList(input: any): Array<IPrintJob> {
    const printJobList: Array<IPrintJob> = [];
    if (input._embedded && input._embedded['px:printJobResources']) {
      for (const printJob of input._embedded['px:printJobResources']) {
        printJobList.push(this.deserializePrintJob(printJob));
      }
    }
    return printJobList;
  }

  public getPrintJobList(): Observable<Array<IPrintJob>> {
    this.logger.info('getPrintJobList()');
    return new Observable((observer) => {

      let httpParams = new HttpParams();

      httpParams = httpParams.set('page', 0);
      httpParams = httpParams.set('pageSize', 1000);
      httpParams = httpParams.set('sort', 'CLOUD_RECEIVED');

      this.httpClient.get<any>(this.userService.userSignal().links.printJobs, { params: httpParams })
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        // return httpErrorResponse.status > 499 ? Observable.of(true) : Observable.throw(httpErrorResponse);
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((response: any) => {
        observer.next(this.deserializePrintJobList(response));
        observer.complete();
      });
    });
  }

  public getSinglePrintJob(printJobUrl: string): Observable<IPrintJob> {
    this.logger.info('getSinglePrintJob()');
    return new Observable((observer) => {
      this.httpClient.get<any>(printJobUrl)
      .pipe(retryWhen(error => error.pipe(
        delay(1000),
        take(3),
        // return httpErrorResponse.status > 499 ? Observable.of(true) : Observable.throw(httpErrorResponse);
        tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
        finalize(() => {
          observer.error(this.httpErrorResponse);
          observer.complete();
        })
      )))
      .subscribe((response: any) => {
        observer.next(this.deserializePrintJob(response));
        observer.complete();
      });
    });
  }

  public getPrintJobs(): Observable<Array<IPrintJob>> {
    this.logger.info('getPrintJobList()');
    return new Observable<Array<IPrintJob>>((observer: Subscriber<Array<IPrintJob>>) => {
      this.getPrintJobList().subscribe((printJobList: Array<IPrintJob>) => {
        observer.next(printJobList);
        observer.complete();
      });
    });
  }

  public getWorkstation(workstationUrl): Observable<any> {
    this.logger.info('getWorkstation()');
    return new Observable<any>((observer) => {
      this.httpClient.get(workstationUrl)
      .subscribe((workstation: any) => {
        // this.dialogService.hideLoadingSpinnerDialog('ModalComponent - getworkstationName()');
        observer.next(workstation);
        observer.complete();
      },(httpErrorResponse: HttpErrorResponse) => {
        this.logger.info('getWorkstation() - httpErrorResponse: ' + httpErrorResponse);
        const errorString = 'Error: ' + httpErrorResponse['error']['errorText'] + ', status: ' + httpErrorResponse['status'];
        observer.error(errorString);
        observer.complete();
      });
    });
  }

  public deleteSelectedPrintJobs = (printJobList): Observable<void> => {
    this.logger.info('deleteSelectedPrintJobs()');
    const objectToSend: Array<any> = [];
    const url: string = this.tenant().links.deletePrintJob;

    _.forEach(printJobList, (printJob) => {
      objectToSend.push({
        printJobId: printJob.links.self,
        permanent: (printJob.deletedTime) ? true : false
      });
    });

    return this.httpClient.post<void>(url, objectToSend)
      .pipe(
        retry({ count: 3, delay: () => timer(1000) })
      );
  };
}
